/* ==========================================================================
   plan-builder.js — "Pick a price. Pick a pace. Pick a date."

   Reads the ship window and the weekly base out of the schedule-data JSON
   that snippets/layaway-plan.liquid renders, so this instrument and the cart
   answer from the same numbers. All arithmetic is LaybyClock's.

   Three controls, one answer. The selected date applies only to this
   calculator. Event reminders are independent of its price, pace and payday.
   ========================================================================== */
(function () {
  'use strict';

  function boot() {
    var clock = window.LaybyClock;
    if (!clock) return;

    document.querySelectorAll('[data-plan-builder]').forEach(function (root) {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';

      var data = clock.readPlan(root.closest('.section') || document) || {};
      var ship = clock.parseShipWindow(data.ship_window);
      var weeklyBase = (data.weekly_base_cents ? data.weekly_base_cents / 100 : 0) || parseInt(root.dataset.weeklyBase, 10) || 25;
      var cap = parseInt(root.dataset.cap, 10) || 3500;

      var transit = parseInt(root.dataset.transitDays, 10);
      if (isNaN(transit)) transit = 5;

      var priceEl = root.querySelector('[data-price]');
      var slider = root.querySelector('[data-slider]');
      var chips = Array.prototype.slice.call(root.querySelectorAll('[data-amount]'));
      var whenEl = root.querySelector('[data-when]');
      var monthsGroup = whenEl ? whenEl.querySelector('[data-months]') : null;
      // The payday control has been both a <select> and a set of chips; handle
      // whichever this section is rendering rather than assuming one.
      var dayEl = root.querySelector('select[data-payday]');
      var dayChips = Array.prototype.slice.call(root.querySelectorAll('button[data-payday]'));
      var presets = Array.prototype.slice.call(root.querySelectorAll('[data-preset]'));
      var road = root.querySelector('[data-road]');

      var out = {
        headline: root.querySelector('[data-out-headline]'),
        first: root.querySelector('[data-out-first]'),
        next: root.querySelector('[data-out-next]'),
        last: root.querySelector('[data-out-last]'),
        months: root.querySelector('[data-out-months]'),
        verdict: root.querySelector('[data-out-verdict]'),
        verdictText: root.querySelector('[data-out-verdict-text]')
      };
      var shipNote = (out.verdict && out.verdict.dataset.shipNote) || 'Then it ships free.';

      var state = {
        price: parseInt(root.dataset.start, 10) || 500,
        weekly: weeklyBase,
        payday: 3,
        occasion: ''      // a block id, 'm:YYYY-MM' for a month end, or '' for none
      };

      /* ---------------------------------------------------------- dates */

      /** A block's date attributes → the next occurrence on or after today. */
      function resolveDate(el) {
        var mode = el.dataset.dateMode, spec = el.dataset.date || '', t = clock.today();
        if (!mode || mode === 'none' || !spec) return null;
        if (mode === 'once') {
          var parts = spec.split('-');
          var once = new Date(+parts[0], +parts[1] - 1, +parts[2]);
          return isNaN(once.getTime()) ? null : once;
        }
        var month = parseInt(spec.slice(0, 2), 10) - 1;
        if (isNaN(month) || month < 0 || month > 11) return null;
        for (var y = t.getFullYear(); y < t.getFullYear() + 3; y++) {
          var d;
          if (mode === 'nth') {
            d = clock.nthWeekday(y, month, Math.max(0, clock.DAYS.indexOf((el.dataset.weekday || 'sun').slice(0, 3).toLowerCase())), +el.dataset.nth || 1);
          } else {
            d = new Date(y, month, parseInt(spec.slice(3, 5), 10) || 1);
          }
          if (d >= t) return d;
        }
        return null;
      }

      // Each occasion option learns its next date: "Christmas · Dec 25".
      var occasionDates = {};
      var whenOptions = whenEl ? Array.prototype.slice.call(whenEl.options) : [];
      whenOptions.forEach(function (opt) {
        if (!opt.value || opt.value === '__any') return;
        var d = resolveDate(opt);
        if (!d) return;
        occasionDates[opt.value] = d;
        opt.textContent = (opt.dataset.title || opt.textContent) + ' \u00b7 ' + clock.fmt(d);
      });

      // "Any month": the end of each of the next six months, so a shopper with
      // a date the occasions do not name still has something to pick — with no
      // date field, no popup and no dd/mm/yyyy chrome.
      var MONTHS_AHEAD = 6;
      if (monthsGroup) {
        var t0 = clock.today();
        for (var i = 1; i <= MONTHS_AHEAD; i++) {
          var endOf = new Date(t0.getFullYear(), t0.getMonth() + i + 1, 0);
          var key = 'm:' + endOf.getFullYear() + '-' + String(endOf.getMonth() + 1).padStart(2, '0');
          occasionDates[key] = endOf;
          var o = document.createElement('option');
          o.value = key;
          o.textContent = 'End of ' + clock.MONTHS[endOf.getMonth()] + (endOf.getFullYear() !== t0.getFullYear() ? ' ' + endOf.getFullYear() : '');
          monthsGroup.appendChild(o);
        }
      }

      function pickedDate() {
        return state.occasion ? (occasionDates[state.occasion] || null) : null;
      }

      var opts = function () { return { weekly: state.weekly, ship: ship, payday: state.payday, transitDays: transit }; };

      /**
       * The weekly amount that would land `price` before `occ` at this payday,
       * rounded up to $5. 0 when no plausible amount does — the date is simply
       * too close — so a caller says "too soon" rather than "$1,500 a week".
       * The slots are every payment on or before the payoff deadline, the one
       * made today included.
       */
      var MAX_SUGGESTED_WEEKLY = 300;
      function weeklyToLand(occ, price) {
        var slots = clock.paymentsBy(clock.payoffDeadline(occ, opts()), opts());
        if (!slots) return 0;
        var need = Math.ceil(price / slots / 5) * 5;
        return need <= MAX_SUGGESTED_WEEKLY ? need : 0;
      }

      /** One verdict for a price at the current pace against a date. */
      function verdictFor(occ, price, p, label) {
        var c = clock.capBefore(occ, opts());
        if (c >= price) {
          return { ok: true, cap: c, text: clock.money(price) + ' lands ' + clock.fmt(p.ship) + (label ? ' · ' + label : ' · before ' + clock.fmt(occ)) };
        }
        var need = weeklyToLand(occ, price);
        return {
          ok: false, cap: c,
          text: clock.money(price) + ' misses ' + clock.fmt(occ) + (need ? ' at ' + clock.money(state.weekly) + ' a week · ' + clock.money(need) + ' a week lands it' : ' · start now for next time')
        };
      }

      if (chips.length) state.weekly = parseInt(chips[0].dataset.amount, 10) || weeklyBase;

      function current() {
        return clock.plan(state.price, { weekly: state.weekly, ship: ship, payday: state.payday });
      }

      function render() {
        var p = current();

        if (out.headline) {
          out.headline.innerHTML =
            '<span class="pb__count">' + p.payments + ' payment' + (p.payments === 1 ? '' : 's') + '</span> of ' +
            clock.money(state.weekly);
        }
        // The first payment is made when the order is placed; the rest come
        // out on the payday, from next week.
        if (out.first) out.first.textContent = 'Today';
        if (out.next) {
          out.next.textContent = p.payments > 1
            ? 'then every ' + clock.DAY_NAMES[p.next.getDay()] + ' from ' + clock.fmt(p.next)
            : '';
        }
        if (out.last) out.last.textContent = clock.fmt(p.last);
        if (out.months) {
          var months = Math.max(1, Math.round(p.payments * 7 / 30.44));
          out.months.textContent = 'about ' + months + ' month' + (months === 1 ? '' : 's');
        }
        if (road && window.LaybyRoad) {
          window.LaybyRoad.draw(road, { payments: p.payments });
        }

        // The last line of the card: the shipping note, or the verdict.
        var occ = pickedDate();
        if (out.verdict && out.verdictText) {
          if (occ) {
            var v = verdictFor(occ, state.price, p, null);
            out.verdictText.textContent = v.text;
            out.verdict.classList.toggle('is-ok', v.ok);
            out.verdict.classList.toggle('is-late', !v.ok);
          } else {
            out.verdictText.textContent = shipNote;
            out.verdict.classList.remove('is-ok', 'is-late');
          }
        }

      }

      function setPrice(v) {
        var n = Math.round(Number(v) || 0);
        state.price = Math.max(50, Math.min(cap, n));
        render();
      }

      function syncSlider() {
        slider.value = Math.max(Number(slider.min), Math.min(Number(slider.max), state.price));
      }

      priceEl.addEventListener('input', function () {
        setPrice(priceEl.value);
        syncSlider();
      });
      priceEl.addEventListener('blur', function () { priceEl.value = state.price; });
      priceEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });

      slider.addEventListener('input', function () {
        setPrice(slider.value);
        priceEl.value = state.price;
      });

      presets.forEach(function (btn) {
        btn.addEventListener('click', function () {
          setPrice(btn.dataset.preset);
          priceEl.value = state.price;
          syncSlider();
        });
      });

      function group(list, attr, apply) {
        list.forEach(function (btn) {
          btn.addEventListener('click', function () {
            list.forEach(function (b) { b.setAttribute('aria-checked', 'false'); });
            btn.setAttribute('aria-checked', 'true');
            apply(btn);
            render();
          });
        });
      }

      group(chips, 'amount', function (btn) {
        state.weekly = parseInt(btn.dataset.amount, 10) || weeklyBase;
      });
      group(dayChips, 'payday', function (btn) {
        state.payday = parseInt(btn.dataset.payday, 10) || 3;
      });

      if (dayEl) {
        dayEl.addEventListener('change', function () {
          state.payday = parseInt(dayEl.value, 10) || 3;
          render();
        });
      }

      // The date: one select, answered at once.
      function applyWhen() {
        state.occasion = whenEl ? whenEl.value : '';
        render();
      }

      if (whenEl) whenEl.addEventListener('change', applyWhen);

      if (road && window.LaybyRoad) {
        window.LaybyRoad.register(road, function () { return { payments: current().payments }; });
      }

      render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
